import { useState, type FormEvent } from 'react';
import { logCall, proposeFromNote } from '../lib/api';
import { STAGES, dateTime, friendlyError, fromLocalInput, inr, stageLabel, toLocalInput, touchLabel } from '../lib/format';
import type { CallProposal, Lead, Stage, TouchKind } from '../lib/types';
import { Button, ErrorText, SelectField, TextArea, TextField } from './ui';
import { useSpeech } from './useSpeech';

// Log a call by typing or speaking. "Fill from note" asks the AI to propose updates; the rep ticks what is
// right and saves. Nothing changes without the rep's tap.
export function CallLogForm({ lead, onDone, onCancel }: { lead: Lead; onDone: () => Promise<void>; onCancel: () => void }) {
  const [kind, setKind] = useState<TouchKind>('call');
  const [note, setNote] = useState('');
  const [proposal, setProposal] = useState<CallProposal | null>(null);
  const [use, setUse] = useState<Record<string, boolean>>({});
  const [nextAt, setNextAt] = useState('');
  const [nextNote, setNextNote] = useState('');
  const [stage, setStage] = useState<Stage | ''>('');
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');
  const speech = useSpeech((t) => setNote((n) => (n ? `${n} ${t}` : t)));

  async function fill() {
    setThinking(true); setError('');
    try {
      const p = await proposeFromNote(lead.id, note);
      setProposal(p);
      setUse({ budget: !!(p.budget_min_inr || p.budget_max_inr), unit_type: !!p.unit_type, horizon: !!p.horizon });
      if (p.next_step_at && p.next_step_note) { setNextAt(toLocalInput(new Date(p.next_step_at))); setNextNote(p.next_step_note); }
      if (p.stage && p.stage !== lead.stage) setStage(p.stage);
    } catch (e) { setError(friendlyError(e)); } finally { setThinking(false); }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const details: Record<string, unknown> = {};
    if (proposal && use.budget) { if (proposal.budget_min_inr) details.budget_min_inr = proposal.budget_min_inr; if (proposal.budget_max_inr) details.budget_max_inr = proposal.budget_max_inr; }
    if (proposal && use.unit_type) details.unit_type = proposal.unit_type;
    if (proposal && use.horizon) details.horizon = proposal.horizon;
    try {
      await logCall(lead.id, kind, note, details, nextAt ? fromLocalInput(nextAt) : null, nextAt ? nextNote : null, (stage || null) as Stage | null);
      await onDone();
    } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <SelectField label="What happened" value={kind} onChange={(e) => setKind(e.target.value as TouchKind)}>
        {(Object.keys(touchLabel) as TouchKind[]).map((k) => <option key={k} value={k}>{touchLabel[k]}</option>)}
      </SelectField>
      <TextArea label="Note" value={note + (speech.interim ? ` ${speech.interim}` : '')} onChange={(e) => setNote(e.target.value)}
        placeholder="Just met Sharma ji, liked the 14th floor, needs a loan, call back Monday" />
      <div className="flex flex-wrap gap-2">
        {speech.supported && (speech.listening
          ? <Button type="button" variant="danger" onClick={speech.stop}>Stop listening</Button>
          : <>
              <Button type="button" onClick={() => speech.start('en-IN')}>Speak (English)</Button>
              <Button type="button" onClick={() => speech.start('hi-IN')}>बोलें (Hindi)</Button>
            </>)}
        <Button type="button" disabled={!note.trim() || thinking} onClick={fill}>{thinking ? 'Reading…' : 'Fill from note'}</Button>
      </div>
      {speech.error && <p className="text-sm text-warn">{speech.error}</p>}

      {proposal && (
        <div className="rounded-xl border border-line bg-surface2 p-3 text-sm">
          <p className="mb-2"><span className="text-muted">AI summary: </span>{proposal.summary}</p>
          {(proposal.budget_min_inr || proposal.budget_max_inr) && (
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!use.budget} onChange={(e) => setUse({ ...use, budget: e.target.checked })} />
              Budget {proposal.budget_min_inr ? `${inr(proposal.budget_min_inr)}–` : 'up to '}{inr(proposal.budget_max_inr)}</label>
          )}
          {proposal.unit_type && <label className="flex items-center gap-2"><input type="checkbox" checked={!!use.unit_type} onChange={(e) => setUse({ ...use, unit_type: e.target.checked })} /> Unit: {proposal.unit_type}</label>}
          {proposal.horizon && <label className="flex items-center gap-2"><input type="checkbox" checked={!!use.horizon} onChange={(e) => setUse({ ...use, horizon: e.target.checked })} /> Timeline: {proposal.horizon}</label>}
          {proposal.next_step_at && <p className="text-muted">Next step suggested for {dateTime(proposal.next_step_at)}: check it below.</p>}
          <p className="mt-1 text-xs text-muted">Untick anything that is wrong. Only what you save is kept.</p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Next step (IST)" type="datetime-local" value={nextAt} onChange={(e) => setNextAt(e.target.value)} />
        <TextField label="Next step note" value={nextNote} onChange={(e) => setNextNote(e.target.value)} required={!!nextAt} />
      </div>
      <SelectField label="Move stage to" value={stage} onChange={(e) => setStage(e.target.value as Stage | '')}>
        <option value="">Keep {stageLabel[lead.stage]}</option>
        {STAGES.filter((s) => s !== 'lost' && s !== 'dormant' && s !== lead.stage).map((s) => <option key={s} value={s}>{stageLabel[s]}</option>)}
      </SelectField>
      <ErrorText>{error}</ErrorText>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Save call'}</Button>
      </div>
    </form>
  );
}
